import { useForm, Controller } from 'react-hook-form';
import { Alert, Box, CircularProgress, FormControl, InputLabel, Modal, Snackbar } from "@mui/material";
import { WhiteInput } from "../../components/Inputs/InputText";
import { BlueHoverButton } from "../../components/Inputs/BlueHoverButton";
import styles from "./Cadastro.module.css";
import { useNavigate } from 'react-router-dom';
import servLogin from '../../services/login/servLogin';
import { useGDDispatch } from '../../hooks';
import { loginCadastro } from '../../state/slices/userSlice';
import { useState } from 'react';
import { style as modalStyle } from "../../components/Loading/Loading";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import InputMask from "react-input-mask";

import { HelperText } from "../../components/Errors/HelperText";
import { validarCpf } from '../../infra/utils/ValidacaoCpf';
import { formatarTelefone, formatarCpf } from '../../infra/utils/FormataValores';

const schema = yup.object().shape({
    Nome: yup.string().required("Este campo é obrigatório").matches("([A-Za-z]( )[A-Za-z]+)", "Deve conter nome e sobrenome (somente letras)").max(100, "Deve conter até 100 caracteres"), 
    Celular: yup.string().required("Este campo é obrigatório"), 
    CPF: yup.string().matches("([0-9]{2}[\.]?[0-9]{3}[\.]?[0-9]{3}[\/]?[0-9]{4}[-]?[0-9]{2})|([0-9]{3}[\.]?[0-9]{3}[\.]?[0-9]{3}[-]?[0-9]{2})", "CPF deve estar no formato correto").required("Este campo é obrigatório"), 
    Email: yup.string().email().required("Este campo é obrigatório").max(100, "Deve conter até 100 caracteres"), 
    Senha: yup.string().matches(
        /^.*(?=.{6,})(?=.*\d)((?=.*[aA-zZ]){1}).*$/,
        "A senha deve conter no mínimo 6 caracteres, contendo letras e números"
    ).required("Este campo é obrigatório"), 
    ConfirmarSenha: yup.string().required("Este campo é obrigatório").oneOf([yup.ref('Senha')], "Senhas devem ser iguais")
});

const Cadastro = () => {
    const [open, setOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const navigate = useNavigate();
    const dispatch = useGDDispatch();
    const { control, handleSubmit, formState: { errors } } = useForm({ resolver: yupResolver(schema) });

    const onClickCadastrar = handleSubmit( async (data) => {
        try {
            setIsLoading(true);

            let cpf = formatarCpf(data.CPF);
            let celular = formatarTelefone(data.Celular);

            if(!validarCpf(cpf)) {
                setIsLoading(false);
                handleAlertError("O CPF informado é inexistente.");

                return;
            }

            if(celular.length < 11) {
                setIsLoading(false);
                handleAlertError("O telefone informado é inválido.");

                return;
            }

            const response = await servLogin.cadastrarUsuario(data.Nome, celular, data.Email, data.Senha, cpf);

            dispatch(loginCadastro(response.Content));

            setIsLoading(false);

            navigate("/concluirCadastro", { state: data });
        } catch (err) {
            setIsLoading(false);
            handleAlertError(err.response.data.Message);
            
            return;
        }
    });

    const onClickVoltar = () => {
        navigate("/");
    }

    const handleAlertError = (message) => {
        setOpen(true);
        setErrorMessage(message);
    }

    const handleClose = (event, reason) => {
        if (reason === 'clickaway') {
            setOpen(false);
        }

        setOpen(false);
    }

    return (
        <main className={styles.cadastroMain}>
            <div className={styles.cadastroContainer}>
                <form onSubmit={onClickCadastrar}>
                <div className={styles.leftCadastroContainer}>
                    <Controller 
                        name='Nome'
                        control={control}
                        render={({field}) => (
                            <FormControl variant="outlined" >
                                <InputLabel htmlFor="cadastro-nome" shrink style={{ color: "#B8B8B8" }} >Nome completo</InputLabel>
                                <WhiteInput 
                                    fullWidth 
                                    placeholder="Nome" 
                                    id="cadastro-nome"
                                    error={!!errors.Nome}
                                    {...field}
                                    className={`${errors?.Nome ? styles.campoComValorErrado : ""}`} 
                                />
                                { errors?.Nome ?  (
                                    <HelperText text={errors?.Nome?.message} />
                                ) : '' }
                            </FormControl>
                        )}
                    />
                    <Controller 
                        name="Celular"
                        control={control}
                        render={({field: {onChange, value}}) => (
                            <FormControl variant="outlined" >
                                <InputLabel htmlFor="cadastro-celular" shrink style={{ color: "#B8B8B8" }} >Celular</InputLabel>
                                <InputMask 
                                    mask="(99) 99999-9999"
                                    onChange={onChange}
                                    value={value}
                                    id="cadastro-celular"
                                >
                                    {inputProps => (
                                        <WhiteInput 
                                            fullWidth
                                            defaultValue=""
                                            error={errors?.Celular?.message}
                                            {...inputProps}
                                            className={`${errors?.Celular ? styles.campoComValorErrado : ""}`} 
                                        />
                                    )}
                                </InputMask>
                                { errors?.Celular ?  (
                                    <HelperText text={errors?.Celular?.message} />
                                ) : '' }
                            </FormControl>
                        )}
                    
                    />
                    <Controller 
                        name="CPF"
                        control={control}
                        render={({field: {onChange, value}}) => (
                            <FormControl variant="outlined" >
                                <InputLabel htmlFor="cadastro-cpf" shrink style={{ color: "#B8B8B8", marginTop: "10px" }} >CPF</InputLabel>
                                <InputMask
                                    mask="999.999.999-99"
                                    onChange={onChange}
                                    value={value}
                                    id="cadastro-cpf"
                                >
                                    {inputProps => (
                                        <WhiteInput 
                                            fullWidth
                                            defaultValue=""
                                            error={errors?.CPF?.message}
                                            {...inputProps}
                                            className={`${errors?.CPF ? styles.campoComValorErrado : ""}`} 
                                            style={{ marginTop: "25px" }}
                                        />
                                    )}
                                </InputMask>
                            { errors?.CPF ?  (
                                <HelperText text={errors?.CPF?.message} />
                            ) : '' }
                            </FormControl>
                            
                        )}
                    />
                </div>
                <div className={styles.rightCadastroContainer}>
                    <Controller 
                        name="Email"
                        control={control}
                        render={({field}) => (
                            <FormControl variant="outlined" >
                                <InputLabel htmlFor="cadastro-email" shrink style={{ color: "#B8B8B8" }} >E-mail</InputLabel>
                                <WhiteInput 
                                    fullWidth 
                                    type="email" 
                                    placeholder="E-mail" 
                                    id="cadastro-email" 
                                    {...field}
                                    error={!!errors.Email}
                                    className={`${errors?.Email ? styles.campoComValorErrado : ""}`} 
                                />
                            { errors?.Email ?  (
                                <HelperText text={errors?.Email?.message} />
                            ) : '' }
                            </FormControl>
                        )}
                    />
                    <Controller
                        name="Senha"
                        control={control}
                        render={({field}) => (
                            <FormControl variant="outlined" >
                                <InputLabel htmlFor="cadastro-senha" shrink style={{ color: "#B8B8B8" }} >Senha</InputLabel>
                                <WhiteInput 
                                    fullWidth 
                                    type="password" 
                                    placeholder="Senha" 
                                    id="cadastro-senha" 
                                    error={!!errors.Senha}
                                    {...field}
                                    className={`${errors?.Senha ? styles.campoComValorErrado : ""}`} 
                                />
                            { errors?.Senha ?  (
                                <HelperText text={errors?.Senha?.message} />
                            ) : '' }
                            </FormControl>
                        )}  
                    />
                    <Controller 
                        name="ConfirmarSenha"
                        control={control}
                        render={({field}) => (
                            <FormControl variant="outlined" >
                                <InputLabel htmlFor="cadastro-confirmacao" shrink style={{ color: "#B8B8B8", marginTop: "10px" }} >Confirmar senha</InputLabel>
                                <WhiteInput 
                                    fullWidth 
                                    type="password" 
                                    placeholder="Confirme sua senha" 
                                    id="cadastro-confirmacao" 
                                    {...field}
                                    style={{ marginTop: "25px" }}
                                    error={!!errors.ConfirmarSenha}
                                    className={`${errors?.ConfirmarSenha ? styles.campoComValorErrado : ""}`} 
                                />
                            { errors?.ConfirmarSenha ?  (
                                <HelperText text={errors?.ConfirmarSenha?.message} />
                            ) : '' }
                            </FormControl>
                        )}
                    />
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <BlueHoverButton variant="contained" onClick={onClickVoltar}>
                        Voltar
                    </BlueHoverButton>
                    <BlueHoverButton variant="contained" type="submit">
                        Concluir
                    </BlueHoverButton>
                    </div>
                </div>
                </form> 
                <Snackbar open={open} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} autoHideDuration={6000} onClose={handleClose}> 
                    <Alert onClose={handleClose} severity="error" sx={{ width: '100%' }}>
                        {errorMessage}
                    </Alert>
                </Snackbar>
                <Modal open={isLoading}>
                    <Box style={modalStyle}>
                        <CircularProgress color="inherit" />
                    </Box>
                </Modal>
            </div>
        </main>
    )
}

export { Cadastro };